import React, { useEffect, useState, useContext } from "react";
import { ThemeContext } from "../../../../context/ThemeContext";
import { getData, postData, postFormData } from "../../../../services/Fetchnodeservices";

// ─────────────────────────────────────────────────────────────────────────────
// ICONS
// ─────────────────────────────────────────────────────────────────────────────
const UsersIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

const RestaurantIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
    <circle cx="12" cy="4" r="1.5" />
  </svg>
);

const RiderIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <circle cx="12" cy="8" r="4" />
    <path d="M5 20v-2a7 7 0 0 1 14 0v2" />
  </svg>
);

const SendIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="22" y1="2" x2="11" y2="13" />
    <polygon points="22 2 15 22 11 13 2 9 22 2" />
  </svg>
);

const ImageIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <rect x="2" y="2" width="20" height="20" rx="2.18" ry="2.18" />
    <circle cx="8.5" cy="8.5" r="1.5" />
    <polyline points="21 15 16 10 5 21" />
  </svg>
);

const LinkIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
  </svg>
);

const ClockIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

const BellIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
  </svg>
);

// ─────────────────────────────────────────────────────────────────────────────
// SUB-COMPONENTS (Theme-Aware)
// ─────────────────────────────────────────────────────────────────────────────

const AudienceCard = ({ title, count, icon, color, theme }) => (
  <div
    style={{
      background: theme.surface,
      border: `1px solid ${theme.border}`,
      borderRadius: "14px",
      padding: "18px",
      flex: 1,
      minWidth: "140px",
      transition: "transform 0.2s ease, box-shadow 0.2s ease",
      cursor: "pointer",
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.transform = "translateY(-2px)";
      e.currentTarget.style.boxShadow = `0 8px 20px ${color}15`;
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.transform = "translateY(0)";
      e.currentTarget.style.boxShadow = "none";
    }}
  >
    <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "12px" }}>
      <div
        style={{
          width: "36px",
          height: "36px",
          borderRadius: "10px",
          background: `${color}1a`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: color,
        }}
      >
        {icon}
      </div>
      <span style={{ fontSize: "13px", fontWeight: 600, color: theme.textPrimary }}>{title}</span>
    </div>
    <div style={{ fontSize: "24px", fontWeight: 700, color: color, marginBottom: "4px" }}>
      {count.toLocaleString()}+
    </div>
    <div style={{ fontSize: "11px", color: theme.textMuted }}>reachable</div>
  </div>
);

const RadioCard = ({ label, description, isSelected, onClick, theme, icon }) => (
  <div
    onClick={onClick}
    style={{
      background: isSelected ? `${theme.primary}15` : "transparent",
      border: `1px solid ${isSelected ? theme.primary : theme.border}`,
      borderRadius: "12px",
      padding: "14px 16px",
      cursor: "pointer",
      transition: "all 0.2s ease",
      display: "flex",
      alignItems: "flex-start",
      gap: "12px",
    }}
    onMouseEnter={(e) => {
      if (!isSelected) {
        e.currentTarget.style.background = theme.surfaceLighter;
        e.currentTarget.style.borderColor = theme.primary;
      }
    }}
    onMouseLeave={(e) => {
      if (!isSelected) {
        e.currentTarget.style.background = "transparent";
        e.currentTarget.style.borderColor = theme.border;
      }
    }}
  >
    <div
      style={{
        width: "18px",
        height: "18px",
        borderRadius: "50%",
        border: `2px solid ${isSelected ? theme.primary : theme.border}`,
        background: isSelected ? theme.primary : "transparent",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        marginTop: "2px",
        transition: "all 0.2s ease",
      }}
    >
      {isSelected && (
        <div
          style={{
            width: "6px",
            height: "6px",
            borderRadius: "50%",
            background: "#fff",
          }}
        />
      )}
    </div>
    {icon && (
      <div
        style={{
          width: "36px",
          height: "36px",
          borderRadius: "8px",
          background: `${theme.primary}15`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: theme.primary,
          flexShrink: 0,
        }}
      >
        {icon}
      </div>
    )}
    <div style={{ flex: 1 }}>
      <div style={{ fontSize: "13px", fontWeight: 600, color: theme.textPrimary, marginBottom: "2px" }}>
        {label}
      </div>
      <div style={{ fontSize: "11px", color: theme.textMuted }}>{description}</div>
    </div>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT (Theme-Aware)
// ─────────────────────────────────────────────────────────────────────────────
const SendNotification = () => {
  const { theme } = useContext(ThemeContext); // ← Theme Context
  
  const [selectedAudience, setSelectedAudience] = useState("users");
  const [deliveryMode, setDeliveryMode] = useState("all");
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [deepLink, setDeepLink] = useState("");
  const [scheduleDate, setScheduleDate] = useState("");
  const [stats, setStats] = useState({ users: 0, restaurants: 0, riders: 0 });
  const [loadingStats, setLoadingStats] = useState(true);
  const [statsError, setStatsError] = useState("");
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);
  const [recipientMode, setRecipientMode] = useState("all");
  const [recipientQuery, setRecipientQuery] = useState("");
  const [recipientResults, setRecipientResults] = useState([]);
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [recipientLoading, setRecipientLoading] = useState(false);
  const [imageUploading, setImageUploading] = useState(false);
  const [imageUploadError, setImageUploadError] = useState("");

  useEffect(() => {
    let active = true;
    const loadStats = async () => {
      setLoadingStats(true);
      setStatsError("");
      const response = await getData("admin/notifications/stats");
      if (!active) return;
      if (response?.status) {
        setStats(response.data || { users: 0, restaurants: 0, riders: 0 });
      } else {
        setStatsError(response?.message || "Could not load Firebase device counts. Deploy and restart the backend, then refresh.");
      }
      setLoadingStats(false);
    };
    loadStats();
    return () => { active = false; };
  }, []);

  const handleSend = async () => {
    if (!title.trim() || !message.trim()) {
      setSendResult({ error: true, message: "Please fill both title and message." });
      return;
    }
    if (deliveryMode === "schedule") {
      setSendResult({ error: true, message: "Scheduled sending is not enabled yet. Choose Send to All or Test Send." });
      return;
    }
    if (recipientMode === "selected" && !selectedRecipients.length) {
      setSendResult({ error: true, message: "Search and select at least one recipient." });
      return;
    }

    setSending(true);
    setSendResult(null);
    const response = await postData("admin/notifications/send", {
      audience: selectedAudience,
      deliveryMode,
      title: title.trim(),
      message: message.trim(),
      imageUrl: imageUrl.trim(),
      deepLink: deepLink.trim(),
      recipientIds: recipientMode === "selected" ? selectedRecipients.map((recipient) => recipient.id) : [],
    });
    setSending(false);

    if (!response?.status) {
      setSendResult({ error: true, message: response?.message || "Notification could not be sent." });
      return;
    }
    setSendResult({ error: false, message: response.message, details: response.data });
    if (deliveryMode === "all") {
      setTitle("");
      setMessage("");
      setImageUrl("");
      setDeepLink("");
    }
  };

  const handleAudienceChange = (audience) => {
    setSelectedAudience(audience);
    setRecipientQuery("");
    setRecipientResults([]);
    setSelectedRecipients([]);
    setSendResult(null);
  };

  const searchRecipients = async () => {
    const query = recipientQuery.trim();
    if (query.length < 2) {
      setRecipientResults([]);
      return;
    }
    setRecipientLoading(true);
    const response = await getData(`admin/notifications/recipients?audience=${encodeURIComponent(selectedAudience)}&query=${encodeURIComponent(query)}`);
    setRecipientLoading(false);
    if (response?.status) {
      setRecipientResults(response.data || []);
    } else {
      setSendResult({ error: true, message: response?.message || "Could not search recipients." });
    }
  };

  const toggleRecipient = (recipient) => {
    setSelectedRecipients((current) => current.some((item) => item.id === recipient.id)
      ? current.filter((item) => item.id !== recipient.id)
      : [...current, recipient]);
  };

  const uploadImage = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setImageUploadError("Please choose an image file.");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setImageUploadError("Image must be 5 MB or smaller.");
      return;
    }
    setImageUploading(true);
    setImageUploadError("");
    const formData = new FormData();
    formData.append("image", file);
    const response = await postFormData("admin/notifications/upload-image", formData);
    setImageUploading(false);
    if (response?.status && response.data?.imageUrl) {
      setImageUrl(response.data.imageUrl);
    } else {
      setImageUploadError(response?.message || "Image upload failed.");
    }
  };

  const audienceConfig = {
    users: { icon: <UsersIcon />, color: theme.primary, label: "Users" },
    restaurants: { icon: <RestaurantIcon />, color: "#ec4899", label: "Restaurants" },
    riders: { icon: <RiderIcon />, color: "#14b8a6", label: "Riders" },
  };

  const styles = {
    container: {
      background: "transparent",
      fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
      padding: "24px",
    },
    header: {
      marginBottom: "28px",
    },
    title: {
      fontSize: "24px",
      fontWeight: 700,
      color: theme.textPrimary,
      marginBottom: "6px",
      display: "flex",
      alignItems: "center",
      gap: "12px",
    },
    subtitle: {
      fontSize: "13px",
      color: theme.textMuted,
    },
    twoColumn: {
      display: "flex",
      gap: "24px",
      flexWrap: "wrap",
      alignItems: "flex-start",
    },
    leftColumn: {
      flex: 1.2,
      minWidth: "340px",
    },
    rightColumn: {
      flex: 0.8,
      minWidth: "300px",
      position: "sticky",
      top: "24px",
    },
    card: {
      background: theme.surface,
      border: `1px solid ${theme.border}`,
      borderRadius: "16px",
      padding: "22px",
      marginBottom: "20px",
      boxShadow: `0 4px 12px ${theme.primary}08`,
    },
    cardTitle: {
      fontSize: "15px",
      fontWeight: 700,
      color: theme.textPrimary,
      marginBottom: "18px",
      display: "flex",
      alignItems: "center",
      gap: "8px",
      paddingBottom: "14px",
      borderBottom: `1px solid ${theme.border}`,
    },
    sectionTitle: {
      fontSize: "11px",
      fontWeight: 700,
      color: theme.textSecondary,
      marginBottom: "12px",
      textTransform: "uppercase",
      letterSpacing: "0.5px",
    },
    audienceTabs: {
      display: "flex",
      gap: "8px",
      marginBottom: "20px",
      flexWrap: "wrap",
      padding: "6px",
      background: theme.surfaceLighter,
      borderRadius: "12px",
      border: `1px solid ${theme.border}`,
    },
    audienceTab: (isActive) => ({
      display: "flex",
      alignItems: "center",
      gap: "8px",
      padding: "10px 20px",
      borderRadius: "8px",
      background: isActive ? theme.primary : "transparent",
      color: isActive ? "#fff" : theme.textSecondary,
      border: "none",
      cursor: "pointer",
      transition: "all 0.2s ease",
      fontSize: "13px",
      fontWeight: 600,
      flex: 1,
      justifyContent: "center",
    }),
    radioGroup: {
      display: "flex",
      flexDirection: "column",
      gap: "10px",
    },
    input: {
      width: "100%",
      padding: "12px 14px",
      background: theme.surfaceLighter,
      border: `1px solid ${theme.border}`,
      borderRadius: "10px",
      fontSize: "13px",
      color: theme.textPrimary,
      outline: "none",
      boxSizing: "border-box",
      transition: "all 0.2s ease",
      fontFamily: "inherit",
    },
    textarea: {
      width: "100%",
      padding: "12px 14px",
      background: theme.surfaceLighter,
      border: `1px solid ${theme.border}`,
      borderRadius: "10px",
      fontSize: "13px",
      color: theme.textPrimary,
      outline: "none",
      fontFamily: "inherit",
      resize: "vertical",
      minHeight: "110px",
      boxSizing: "border-box",
      transition: "all 0.2s ease",
      lineHeight: 1.5,
    },
    label: {
      display: "block",
      fontSize: "12px",
      fontWeight: 600,
      color: theme.textSecondary,
      marginBottom: "6px",
      textTransform: "uppercase",
      letterSpacing: "0.5px",
    },
    charCount: {
      fontSize: "11px",
      color: message.length > 270 ? theme.warning : theme.textMuted,
      textAlign: "right",
      marginTop: "6px",
      fontWeight: 500,
    },
    previewCard: {
      background: `linear-gradient(135deg, ${theme.primary}15 0%, ${theme.primary}05 100%)`,
      borderRadius: "14px",
      padding: "18px",
      marginBottom: "16px",
      border: `1px solid ${theme.border}`,
      position: "relative",
      overflow: "hidden",
    },
    previewBadge: {
      position: "absolute",
      top: "12px",
      right: "12px",
      padding: "2px 8px",
      borderRadius: "10px",
      background: `${theme.primary}20`,
      color: theme.primary,
      fontSize: "10px",
      fontWeight: 700,
      textTransform: "uppercase",
      letterSpacing: "0.5px",
    },
    previewTitle: {
      fontSize: "14px",
      fontWeight: 700,
      color: theme.textPrimary,
      marginBottom: "8px",
      paddingRight: "60px",
    },
    previewMessage: {
      fontSize: "13px",
      color: theme.textSecondary,
      lineHeight: 1.6,
    },
    previewImage: {
      width: "100%",
      height: "120px",
      objectFit: "cover",
      borderRadius: "8px",
      marginTop: "12px",
      border: `1px solid ${theme.border}`,
    },
    sendBtn: {
      width: "100%",
      padding: "13px",
      background: theme.primary,
      color: "#fff",
      border: "none",
      borderRadius: "10px",
      fontSize: "14px",
      fontWeight: 600,
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "8px",
      marginTop: "16px",
      boxShadow: `0 4px 12px ${theme.primary}40`,
      transition: "all 0.2s ease",
    },
    scheduleBtn: {
      width: "100%",
      padding: "11px",
      background: "transparent",
      color: theme.textSecondary,
      border: `1px solid ${theme.border}`,
      borderRadius: "10px",
      fontSize: "13px",
      fontWeight: 500,
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "8px",
      marginTop: "8px",
      transition: "all 0.2s ease",
    },
    audienceCards: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))",
      gap: "12px",
      marginBottom: "20px",
    },
    infoItem: {
      display: "flex",
      alignItems: "flex-start",
      gap: "10px",
      padding: "10px 0",
      borderBottom: `1px solid ${theme.border}`,
    },
    infoDot: {
      width: "6px",
      height: "6px",
      borderRadius: "50%",
      background: theme.primary,
      flexShrink: 0,
      marginTop: "6px",
    },
    infoText: {
      fontSize: "12px",
      color: theme.textSecondary,
      lineHeight: 1.5,
    },
  };

  return (
    <div style={styles.container}>
      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>

      {/* Header */}
      <div style={styles.header}>
        <h1 style={styles.title}>
          <div
            style={{
              width: "44px",
              height: "44px",
              borderRadius: "12px",
              background: `linear-gradient(135deg, ${theme.primary}, ${theme.primary}cc)`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#fff",
              boxShadow: `0 4px 12px ${theme.primary}40`,
            }}
          >
            <BellIcon />
          </div>
          Send Notification
        </h1>
        <p style={styles.subtitle}>
          Broadcast push notifications to your users, restaurants, and riders
        </p>
      </div>

      <div style={styles.twoColumn}>
        {/* Left Column */}
        <div style={styles.leftColumn}>
          {/* Audience Selection */}
          <div style={styles.card}>
            <div style={styles.cardTitle}>
              🎯 Select Audience
            </div>

            <div style={styles.audienceTabs}>
              {Object.entries(audienceConfig).map(([key, config]) => (
                <button
                  key={key}
                  style={styles.audienceTab(selectedAudience === key)}
                  onClick={() => handleAudienceChange(key)}
                  onMouseEnter={(e) => {
                    if (selectedAudience !== key) {
                      e.currentTarget.style.background = theme.surface;
                      e.currentTarget.style.color = theme.textPrimary;
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (selectedAudience !== key) {
                      e.currentTarget.style.background = "transparent";
                      e.currentTarget.style.color = theme.textSecondary;
                    }
                  }}
                >
                  {config.icon} {config.label}
                </button>
              ))}
            </div>

            {/* Audience Stats */}
            <div style={styles.sectionTitle}>Audience Reach</div>
            <div style={styles.audienceCards}>
              <AudienceCard
                title="Users"
                count={stats.users}
                icon={<UsersIcon />}
                color={theme.primary}
                theme={theme}
              />
              <AudienceCard
                title="Restaurants"
                count={stats.restaurants}
                icon={<RestaurantIcon />}
                color="#ec4899"
                theme={theme}
              />
              <AudienceCard
                title="Riders"
                count={stats.riders}
                icon={<RiderIcon />}
                color="#14b8a6"
                theme={theme}
              />
            </div>

            {statsError && (
              <div
                role="alert"
                style={{
                  marginBottom: "14px",
                  padding: "10px 12px",
                  borderRadius: "8px",
                  fontSize: "12px",
                  color: theme.error || "#dc2626",
                  background: "#fef2f2",
                  border: "1px solid #fecaca",
                }}
              >
                {statsError}
              </div>
            )}

            <div
              style={{
                padding: "12px 14px",
                background: `${audienceConfig[selectedAudience].color}10`,
                border: `1px solid ${audienceConfig[selectedAudience].color}30`,
                borderRadius: "10px",
                fontSize: "12px",
                color: audienceConfig[selectedAudience].color,
                fontWeight: 500,
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <span style={{ fontSize: "14px" }}>📢</span>
              Your notification will be sent to{" "}
              <strong>
                {loadingStats ? "Loading…" : `${(stats[selectedAudience] || 0).toLocaleString()} ${audienceConfig[selectedAudience].label}`}
              </strong>
            </div>

            <div style={{ marginTop: "18px" }}>
              <div style={styles.sectionTitle}>Who should receive it?</div>
              <div style={{ display: "flex", gap: "10px", marginBottom: "12px" }}>
                <button type="button" onClick={() => setRecipientMode("all")} style={{ ...styles.audienceTab(recipientMode === "all"), flex: "unset", padding: "9px 14px" }}>Everyone</button>
                <button type="button" onClick={() => setRecipientMode("selected")} style={{ ...styles.audienceTab(recipientMode === "selected"), flex: "unset", padding: "9px 14px" }}>Selected people</button>
              </div>
              {recipientMode === "selected" && (
                <div style={{ animation: "fadeIn 0.2s ease" }}>
                  <div style={{ display: "flex", gap: "8px" }}>
                    <input
                      type="search"
                      style={styles.input}
                      placeholder={`Search ${audienceConfig[selectedAudience].label.toLowerCase()} by name, phone or ID`}
                      value={recipientQuery}
                      onChange={(event) => setRecipientQuery(event.target.value)}
                      onKeyDown={(event) => { if (event.key === "Enter") { event.preventDefault(); searchRecipients(); } }}
                    />
                    <button type="button" onClick={searchRecipients} disabled={recipientLoading} style={{ border: "none", borderRadius: "10px", padding: "0 14px", background: theme.primary, color: "#fff", cursor: "pointer" }}>
                      {recipientLoading ? "…" : "Search"}
                    </button>
                  </div>
                  {selectedRecipients.length > 0 && (
                    <div style={{ marginTop: "10px", fontSize: "12px", color: theme.primary, fontWeight: 600 }}>
                      Selected: {selectedRecipients.map((recipient) => recipient.name).join(", ")}
                    </div>
                  )}
                  {recipientResults.length > 0 && (
                    <div style={{ marginTop: "10px", maxHeight: "190px", overflowY: "auto", border: `1px solid ${theme.border}`, borderRadius: "9px" }}>
                      {recipientResults.map((recipient) => {
                        const selected = selectedRecipients.some((item) => item.id === recipient.id);
                        return (
                          <label key={recipient.id} style={{ display: "flex", alignItems: "center", gap: "10px", padding: "10px 12px", borderBottom: `1px solid ${theme.border}`, cursor: "pointer", fontSize: "12px", color: theme.textPrimary }}>
                            <input type="checkbox" checked={selected} onChange={() => toggleRecipient(recipient)} />
                            <span style={{ flex: 1 }}><strong>{recipient.name}</strong>{recipient.phone ? ` · ${recipient.phone}` : ""}</span>
                            <span style={{ color: theme.textMuted }}>#{recipient.id}</span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Delivery Mode */}
          <div style={styles.card}>
            <div style={styles.cardTitle}>📦 Delivery Mode</div>
            <div style={styles.radioGroup}>
              <RadioCard
                label="Send to All"
                description="Broadcast to entire selected audience immediately"
                isSelected={deliveryMode === "all"}
                onClick={() => setDeliveryMode("all")}
                theme={theme}
                icon={<SendIcon />}
              />
              <RadioCard
                label="Test Send"
                description="Send to one registered device in this audience"
                isSelected={deliveryMode === "test"}
                onClick={() => setDeliveryMode("test")}
                theme={theme}
                icon={<UsersIcon />}
              />
              <RadioCard
                label="Schedule"
                description="Send at a specific date and time"
                isSelected={deliveryMode === "schedule"}
                onClick={() => setDeliveryMode("schedule")}
                theme={theme}
                icon={<ClockIcon />}
              />
            </div>
            {deliveryMode === "schedule" && (
              <div style={{ marginTop: "16px", animation: "fadeIn 0.3s ease" }}>
                <label style={styles.label}>Schedule Date & Time</label>
                <input
                  type="datetime-local"
                  style={styles.input}
                  value={scheduleDate}
                  onChange={(e) => setScheduleDate(e.target.value)}
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = theme.primary;
                    e.currentTarget.style.boxShadow = `0 0 0 3px ${theme.primary}15`;
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = theme.border;
                    e.currentTarget.style.boxShadow = "none";
                  }}
                />
              </div>
            )}
          </div>

          {/* Notification Composition */}
          <div style={styles.card}>
            <div style={styles.cardTitle}>✏️ Compose Notification</div>

            <div style={{ marginBottom: "18px" }}>
              <label style={styles.label}>
                Title <span style={{ color: theme.error || theme.danger }}>*</span>
              </label>
              <input
                type="text"
                style={styles.input}
                placeholder="Enter notification title..."
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                maxLength={60}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = theme.primary;
                  e.currentTarget.style.boxShadow = `0 0 0 3px ${theme.primary}15`;
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = theme.border;
                  e.currentTarget.style.boxShadow = "none";
                }}
              />
              <div style={{ ...styles.charCount, color: title.length > 50 ? theme.warning : theme.textMuted }}>
                {title.length}/60 characters
              </div>
            </div>

            <div style={{ marginBottom: "18px" }}>
              <label style={styles.label}>
                Message <span style={{ color: theme.error || theme.danger }}>*</span>
              </label>
              <textarea
                style={styles.textarea}
                placeholder="Write your notification message..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                maxLength={300}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = theme.primary;
                  e.currentTarget.style.boxShadow = `0 0 0 3px ${theme.primary}15`;
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = theme.border;
                  e.currentTarget.style.boxShadow = "none";
                }}
              />
              <div style={styles.charCount}>{message.length}/300 characters</div>
            </div>

            <div style={{ marginBottom: "18px" }}>
              <label style={styles.label}>Poster / Image (optional)</label>
              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <div
                  style={{
                    width: "40px",
                    height: "40px",
                    borderRadius: "10px",
                    background: `${theme.primary}15`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: theme.primary,
                    flexShrink: 0,
                  }}
                >
                  <ImageIcon />
                </div>
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/svg+xml"
                  style={{ ...styles.input, flex: 1, padding: "9px 12px" }}
                  onChange={(event) => uploadImage(event.target.files?.[0])}
                />
              </div>
              <div style={{ fontSize: "11px", color: imageUploadError ? (theme.error || "#dc2626") : theme.textMuted, marginTop: "7px" }}>
                {imageUploading ? "Uploading image…" : imageUploadError || (imageUrl ? "Image uploaded successfully." : "JPG, PNG, WebP or SVG · maximum 5 MB")}
              </div>
            </div>

            <div>
              <label style={styles.label}>Deep Link (optional)</label>
              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <div
                  style={{
                    width: "40px",
                    height: "40px",
                    borderRadius: "10px",
                    background: `${theme.primary}15`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: theme.primary,
                    flexShrink: 0,
                  }}
                >
                  <LinkIcon />
                </div>
                <input
                  type="text"
                  style={{ ...styles.input, flex: 1 }}
                  placeholder="app://screen/order/123"
                  value={deepLink}
                  onChange={(e) => setDeepLink(e.target.value)}
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = theme.primary;
                    e.currentTarget.style.boxShadow = `0 0 0 3px ${theme.primary}15`;
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = theme.border;
                    e.currentTarget.style.boxShadow = "none";
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Right Column */}
        <div style={styles.rightColumn}>
          {/* Preview Card */}
          <div style={styles.card}>
            <div style={styles.cardTitle}>👁️ Live Preview</div>
            <div style={styles.previewCard}>
              <div style={styles.previewBadge}>Preview</div>
              <div style={styles.previewTitle}>
                {title || "Notification Title"}
              </div>
              <div style={styles.previewMessage}>
                {message || "Your message will appear here..."}
              </div>
              {imageUrl && (
                <img
                  src={imageUrl}
                  alt="Preview"
                  style={styles.previewImage}
                  onError={(e) => {
                    e.target.style.display = "none";
                  }}
                />
              )}
              {deepLink && (
                <div
                  style={{
                    marginTop: "12px",
                    padding: "8px 12px",
                    background: theme.surface,
                    borderRadius: "8px",
                    fontSize: "11px",
                    color: theme.primary,
                    fontFamily: "monospace",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    border: `1px solid ${theme.border}`,
                  }}
                >
                  <LinkIcon /> {deepLink}
                </div>
              )}
            </div>

            <button
              type="button"
              disabled={sending || loadingStats || !(stats[selectedAudience] || 0)}
              style={{
                ...styles.sendBtn,
                opacity: sending || loadingStats || !(stats[selectedAudience] || 0) ? 0.6 : 1,
                cursor: sending || loadingStats || !(stats[selectedAudience] || 0) ? "not-allowed" : "pointer",
              }}
              onClick={handleSend}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-1px)";
                e.currentTarget.style.boxShadow = `0 6px 16px ${theme.primary}60`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = `0 4px 12px ${theme.primary}40`;
              }}
            >
              <SendIcon /> {sending ? "Sending…" : deliveryMode === "test" ? "Send Test Notification" : "Send Notification"}
            </button>
            {sendResult && (
              <div
                role="status"
                style={{
                  marginTop: "12px",
                  padding: "10px 12px",
                  borderRadius: "8px",
                  fontSize: "12px",
                  lineHeight: 1.5,
                  color: sendResult.error ? (theme.error || "#dc2626") : "#15803d",
                  background: sendResult.error ? "#fef2f2" : "#f0fdf4",
                  border: `1px solid ${sendResult.error ? "#fecaca" : "#bbf7d0"}`,
                }}
              >
                {sendResult.message}
                {!sendResult.error && sendResult.details && (
                  <div style={{ marginTop: "3px", opacity: 0.8 }}>
                    {sendResult.details.successCount}/{sendResult.details.targetedCount} delivered
                    {sendResult.details.failureCount ? ` · ${sendResult.details.failureCount} failed` : ""}
                  </div>
                )}
              </div>
            )}
            <button
              style={styles.scheduleBtn}
              onClick={() => setDeliveryMode("schedule")}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = theme.surfaceLighter;
                e.currentTarget.style.color = theme.primary;
                e.currentTarget.style.borderColor = theme.primary;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "transparent";
                e.currentTarget.style.color = theme.textSecondary;
                e.currentTarget.style.borderColor = theme.border;
              }}
            >
              <ClockIcon /> Schedule for later
            </button>
          </div>

          {/* Quick Info */}
          <div style={styles.card}>
            <div style={styles.cardTitle}>ℹ️ Quick Info</div>
            <div>
              {[
                "Notifications will be sent instantly to selected audience",
                "You can schedule notifications for later delivery",
                "Add images to make notifications more engaging",
                "Deep links can direct users to specific screens",
                "Test sends help verify content before broadcast",
              ].map((text, idx) => (
                <div key={idx} style={styles.infoItem}>
                  <div style={styles.infoDot} />
                  <div style={styles.infoText}>{text}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Tips Card */}
          <div
            style={{
              ...styles.card,
              background: `linear-gradient(135deg, ${theme.warning}15 0%, ${theme.warning}05 100%)`,
              border: `1px solid ${theme.warning}30`,
            }}
          >
            <div
              style={{
                fontSize: "14px",
                fontWeight: 700,
                color: theme.warning,
                marginBottom: "10px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              💡 Pro Tips
            </div>
            <div style={{ fontSize: "12px", color: theme.textSecondary, lineHeight: 1.6 }}>
              Keep titles under 40 chars for best visibility. Use emojis sparingly to boost engagement.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SendNotification;
